import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { IoCloseOutline } from 'react-icons/io5';
import { FiBell } from 'react-icons/fi';
import NotificationService from '../api/service/member/NotificationService';
import { NotificationsListData } from '../api/service/member/NotificationInterfaces';
import { formatDateWithTime } from '../utils/formatDateWithTime';
import { useNavigate } from 'react-router';
import { AdminPageUrl, MyPageUrl, NoticePageUrl } from '../url/poenUrl';
import { IoClose } from 'react-icons/io5';

interface NotificationPopupProps {
  onClose: () => void;
}

const PopupContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupBox = styled.div`
  background-color: ${(props) => props.theme.colors.bgColor};
  width: 100%;
  max-width: 500px;
  max-height: 300px;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;

  @media (max-width: 968px) {
    max-height: 100vh;
    height: 100vh;
    max-width: 100%;
  }
`;

const PopupContent = styled.div`
  background-color: ${(props) => props.theme.colors.bgColor};
  border-radius: 20px;
  padding: 20px 20px 10px 20px;
  border: 3px solid #e2ece9;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${(props) => props.theme.colors.textColor};

  svg {
    font-size: 20px;
  }
`;

const CloseButton = styled(IoCloseOutline)`
  position: absolute;
  top: 30px;
  right: 30px;
  font-size: 24px;
  cursor: pointer;
  color: #999;
`;

const NotificationList = styled.ul`
  list-style: none;
  max-height: 175px;
  padding: 0;
  margin: 0;
  overflow-y: auto;

  @media (max-width: 968px) {
    max-height: 85vh;
    height: 100vh;
    max-width: 100%;
  }
`;

const NotificationItem = styled.li`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  gap: 10px;
  flex-direction: column;
  position: relative;
  &:hover {
    background-color: #f0f0f0;
  }
`;

const NoMessage = styled.div`
  text-align: center;
  font-size: 14px;
  color: ${(props) => props.theme.colors.textColor};
  word-break: keep-all;
`;

const Message = styled.div`
  text-align: center;
  font-size: 14px;
  color: ${(props) => props.theme.colors.textColor};
  word-break: keep-all;
`;

const Date = styled.div`
  font-size: 12px;
  margin-top: 2px;
  color: #999;
  word-break: keep-all;
`;

const New = styled.span`
  color: red;
  font-size: 20px;
`;
const DeleteButton = styled.span`
  position: absolute;
  right: 10px;
  &:hover {
    color: red;
  }
`;

const NotificationPopup: React.FC<NotificationPopupProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationsListData[]>(
    []
  );
  const [permissions, setPermissions] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const storedPermissions = localStorage.getItem('permissions') || 'default'; // 기본값 설정
    setPermissions(storedPermissions);
  }, []);

  const fetchNotifications = async (page: number) => {
    try {
      const data = await NotificationService.getNotificationsList(page, 10);
      if (data.data.content.length === 0) {
        setHasMore(false);
      } else {
        setNotifications((prev) => [...prev, ...data.data.content]);
      }
    } catch (error) {
      console.error('알림 목록 조회 실패:', error);
    }
  };

  useEffect(() => {
    fetchNotifications(page);
  }, [page]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1.0 }
    );

    if (loader.current) {
      observer.observe(loader.current);
    }

    return () => {
      if (loader.current) {
        observer.unobserve(loader.current);
      }
    };
  }, [hasMore]);

  const handleRowClick = async (notificationId: string) => {
    try {
      const response =
        await NotificationService.getNotificationDetail(notificationId);

      const currentUrl = new URL(window.location.href);
      const linkTo = response.data.linkTo;

      // permissions에 따라 다른 페이지로 이동
      if (permissions === 'ADMIN') {
        const targetUrl = `${AdminPageUrl}?activeTab=2&questionId=${linkTo}`;
        if (`${currentUrl.pathname}${currentUrl.search}` === targetUrl) {
          window.location.reload();
        } else {
          navigate(targetUrl);
        }
      } else if (permissions === 'SELLER') {
        if (response.data.type === 'INQUIRY') {
          const targetUrl = `${MyPageUrl}?activeTab=0&questionId=${linkTo}`;
          if (`${currentUrl.pathname}${currentUrl.search}` === targetUrl) {
            window.location.reload();
          } else {
            navigate(targetUrl);
          }
        } else {
          const targetUrl = `${NoticePageUrl}?activeTab=1&questionId=${linkTo}`;
          if (`${currentUrl.pathname}${currentUrl.search}` === targetUrl) {
            window.location.reload();
          } else {
            navigate(targetUrl);
          }
        }
      } else if (permissions === 'BUYER') {
        const targetUrl = `${NoticePageUrl}?activeTab=1&questionId=${linkTo}`;
        if (`${currentUrl.pathname}${currentUrl.search}` === targetUrl) {
          window.location.reload();
        } else {
          navigate(targetUrl);
        }
      }
    } catch (error) {
      console.error('공지사항 상세 조회 실패:', error);
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await NotificationService.deleteNotification(notificationId);
      setNotifications((prev) =>
        prev.filter(
          (notification) => notification.notificationId !== notificationId
        )
      );
      alert('알림을 삭제했습니다.');
    } catch (error) {
      console.error('알림 삭제 실패:', error);
      alert(error);
    }
  };

  return (
    <PopupContainer>
      <PopupBox>
        <PopupContent>
          <TitleContainer>
            <Title>
              <FiBell style={{ color: '#999' }} />
              알림 메시지
            </Title>
            <CloseButton onClick={onClose} />
          </TitleContainer>

          <NotificationList>
            {notifications.length === 0 && (
              <NoMessage>알림이 없습니다.</NoMessage>
            )}
            {notifications.map((notification, index) => (
              <NotificationItem
                key={index}
                onClick={() => handleRowClick(notification.notificationId)}
              >
                <Message>
                  {notification.title}에 {notification.body}
                  {!notification.isRead && <New>•</New>}
                </Message>
                <Date>{formatDateWithTime(notification.regDate)}</Date>
                <DeleteButton
                  onClick={(e) => {
                    e.stopPropagation(); // 클릭 이벤트 전파 방지
                    handleDelete(notification.notificationId);
                  }}
                >
                  <IoClose />
                </DeleteButton>
              </NotificationItem>
            ))}
          </NotificationList>
          {hasMore && <div ref={loader} style={{ height: '20px' }} />}
        </PopupContent>
      </PopupBox>
    </PopupContainer>
  );
};

export default NotificationPopup;
